import { useEffect, useRef } from "react";
import { siteConfig } from "@/lib/site-config";

interface FormEmbedProps {
  /** Placement prefix — the widget id is appended, e.g. "inline" | "popup". */
  placement?: string;
  className?: string;
}

/**
 * Embeds the LeadConnector form widget exactly as provided in the
 * business widget code. We never force a height, scroll, or
 * compression — the form_embed.js script controls iframe height.
 */
const FormEmbed = ({ placement = "inline", className = "" }: FormEmbedProps) => {
  const id = `${placement}-${siteConfig.widgets.formId}`;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    } else {
      // Script already loaded — re-run any global init if available
      // form_embed.js auto-attaches via postMessage, so simply remounting
      // the iframe is enough for it to start measuring.
      try {
        // @ts-expect-error - global form embed init
        if (typeof window.formEmbedInit === "function") window.formEmbedInit();
      } catch {
        // noop
      }
    }
  }, []);

  return (
    <div ref={containerRef} className={className}>
      <iframe
        src={siteConfig.widgets.formIframeSrc}
        style={{ width: "100%", border: "none", borderRadius: 10, display: "block" }}
        id={id}
        data-layout="{'id':'INLINE'}"
        data-trigger-type="alwaysShow"
        data-trigger-value=""
        data-activation-type="alwaysActivated"
        data-activation-value=""
        data-deactivation-type="neverDeactivate"
        data-deactivation-value=""
        data-form-name={siteConfig.widgets.formName}
        data-height={siteConfig.widgets.formHeight}
        data-layout-iframe-id={id}
        data-form-id={siteConfig.widgets.formId}
        title={siteConfig.widgets.formName}
        scrolling="no"
      />
    </div>
  );
};

export default FormEmbed;
